import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, Globe, MapPin, Paperclip, Loader2, X, PhoneCall } from "lucide-react";
import { SectionHeading } from "./SectionHeading";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_TYPES = [
  "Isolation intérieure",
  "Plaquisterie / cloisons",
  "Faux plafond",
  "Finitions",
  "Aménagement intérieur",
  "Rénovation intérieure complète",
  "Autre",
];

const EMPTY = {
  nom: "", email: "", telephone: "", ville: "",
  type_projet: "", surface: "", date_souhaitee: "", message: "",
};

const inputCls =
  "w-full border border-mineral bg-casse/40 px-4 py-3 text-sm text-anthracite placeholder:text-anthracite/40 focus:border-nuit focus:outline-none transition-colors duration-300";

const Field = ({ label, htmlFor, children, full = false }) => (
  <div className={full ? "md:col-span-2" : ""}>
    <label htmlFor={htmlFor} className="mb-2 block font-accent text-[11px] font-medium tracking-[0.18em] uppercase text-anthracite/70">
      {label}
    </label>
    {children}
  </div>
);

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onFiles = (e) => {
    const list = Array.from(e.target.files || []).slice(0, 5);
    setFiles(list);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      files.forEach((f) => fd.append("fichiers", f));
      await axios.post(`${API}/contact`, fd);
      toast.success("Votre demande a bien été envoyée. Nous revenons vers vous rapidement.");
      setForm(EMPTY);
      setFiles([]);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "L’envoi a échoué. Réessayez ou contactez-nous par téléphone.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <SectionHeading
          chapter="06 — Contact"
          title="Parlons de votre projet"
          subtitle="Vous avez un projet d’isolation, de plaquisterie, de faux plafond ou d’aménagement intérieur ? Décrivez-nous votre besoin afin que nous puissions organiser un premier échange."
          testId="contact"
        />

        <Reveal delay={0.05}>
          <div
            data-testid="contact-callback-note"
            className="mb-10 flex items-center gap-4 border-l-2 border-sable bg-white px-6 py-5"
          >
            <PhoneCall size={20} strokeWidth={1.5} className="shrink-0 text-sable-deep" aria-hidden="true" />
            <p className="text-sm md:text-base leading-relaxed text-anthracite/80">
              <span className="font-accent font-medium text-nuit">Réactivité :</span> nous vous
              rappelons <span className="font-accent font-medium text-nuit">en moins de 48 h</span> pour
              organiser un premier échange.
            </p>
          </div>
        </Reveal>

        <div className="grid lg:grid-cols-5 border border-mineral" data-testid="contact-panel">
          <Reveal className="lg:col-span-2">
            <div className="flex h-full flex-col justify-between gap-12 bg-nuit p-8 lg:p-12">
              <div className="space-y-7">
                <div className="flex items-start gap-4">
                  <Phone size={18} strokeWidth={1.5} className="mt-0.5 text-sable" aria-hidden="true" />
                  <div>
                    <p className="font-accent text-[11px] tracking-[0.24em] uppercase text-casse/50">Téléphone</p>
                    <a href="tel:+33786256965" data-testid="contact-phone" className="mt-1 block font-heading text-lg font-semibold text-casse hover:text-sable transition-colors duration-300">
                      07 86 25 69 65
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Mail size={18} strokeWidth={1.5} className="mt-0.5 text-sable" aria-hidden="true" />
                  <div>
                    <p className="font-accent text-[11px] tracking-[0.24em] uppercase text-casse/50">E-mail</p>
                    <a href="mailto:contact@limen-ar.fr" data-testid="contact-email" className="mt-1 block text-sm text-casse hover:text-sable transition-colors duration-300">
                      contact@limen-ar.fr
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Globe size={18} strokeWidth={1.5} className="mt-0.5 text-sable" aria-hidden="true" />
                  <div>
                    <p className="font-accent text-[11px] tracking-[0.24em] uppercase text-casse/50">Site</p>
                    <p className="mt-1 text-sm text-casse" data-testid="contact-website">limen-ar.fr</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <MapPin size={18} strokeWidth={1.5} className="mt-0.5 text-sable" aria-hidden="true" />
                  <div>
                    <p className="font-accent text-[11px] tracking-[0.24em] uppercase text-casse/50">Secteur</p>
                    <p className="mt-1 text-sm text-casse" data-testid="contact-zone">Basée en Seine-et-Marne — chaque projet est étudié, même au-delà</p>
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-3">
                <a
                  href="tel:+33786256965"
                  data-testid="contact-call-button"
                  className="bg-sable px-6 py-4 text-center font-accent text-sm font-medium tracking-[0.12em] uppercase text-nuit hover:bg-casse transition-colors duration-300"
                >
                  07 86 25 69 65
                </a>
                <a
                  href="mailto:contact@limen-ar.fr"
                  data-testid="contact-mail-button"
                  className="border border-casse/30 px-6 py-4 text-center font-accent text-sm font-medium tracking-[0.12em] uppercase text-casse hover:border-sable hover:text-sable transition-colors duration-300"
                >
                  Envoyer un e-mail
                </a>
              </div>
            </div>
          </Reveal>

          <Reveal className="lg:col-span-3" delay={0.12}>
            <form id="contact-form" data-testid="contact-form" onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-white p-8 lg:p-12">
              <Field label="Nom / Prénom *" htmlFor="nom">
                <input id="nom" data-testid="form-nom" required value={form.nom} onChange={set("nom")} className={inputCls} placeholder="Votre nom" />
              </Field>
              <Field label="E-mail *" htmlFor="email">
                <input id="email" type="email" data-testid="form-email" required value={form.email} onChange={set("email")} className={inputCls} placeholder="vous@exemple.fr" />
              </Field>
              <Field label="Téléphone *" htmlFor="telephone">
                <input id="telephone" type="tel" data-testid="form-telephone" required value={form.telephone} onChange={set("telephone")} className={inputCls} placeholder="06 00 00 00 00" />
              </Field>
              <Field label="Ville du chantier" htmlFor="ville">
                <input id="ville" data-testid="form-ville" value={form.ville} onChange={set("ville")} className={inputCls} placeholder="Melun, Fontainebleau…" />
              </Field>
              <Field label="Type de projet" htmlFor="type_projet">
                <select id="type_projet" data-testid="form-type-projet" value={form.type_projet} onChange={set("type_projet")} className={inputCls}>
                  <option value="">Sélectionner…</option>
                  {PROJECT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Surface approximative" htmlFor="surface">
                <input id="surface" data-testid="form-surface" value={form.surface} onChange={set("surface")} className={inputCls} placeholder="Ex. 45 m²" />
              </Field>
              <Field label="Date souhaitée" htmlFor="date_souhaitee" full>
                <input id="date_souhaitee" data-testid="form-date" value={form.date_souhaitee} onChange={set("date_souhaitee")} className={inputCls} placeholder="Ex. septembre 2026" />
              </Field>
              <Field label="Message" htmlFor="message" full>
                <textarea id="message" data-testid="form-message" rows={5} value={form.message} onChange={set("message")} className={`${inputCls} resize-y`} placeholder="Décrivez votre projet, vos contraintes, vos attentes…" />
              </Field>

              <div className="md:col-span-2">
                <span className="mb-2 block font-accent text-[11px] font-medium tracking-[0.18em] uppercase text-anthracite/70">
                  Ajouter plans / photos
                </span>
                <label
                  htmlFor="fichiers"
                  data-testid="form-files-dropzone"
                  className="flex cursor-pointer items-center justify-center gap-3 border border-dashed border-mineral bg-casse/40 px-4 py-6 text-sm text-anthracite/60 hover:border-sable-deep hover:text-nuit transition-colors duration-300"
                >
                  <Paperclip size={16} strokeWidth={1.5} aria-hidden="true" />
                  Joindre des fichiers (PDF, images — 5 max, 10 Mo chacun)
                </label>
                <input id="fichiers" data-testid="form-files" type="file" multiple accept=".pdf,image/*" onChange={onFiles} className="sr-only" />
                {files.length > 0 && (
                  <ul className="mt-3 space-y-2" data-testid="form-files-list">
                    {files.map((f, i) => (
                      <li key={`${f.name}-${i}`} className="flex items-center justify-between border border-mineral bg-casse/40 px-3 py-2 text-xs text-anthracite/75">
                        <span className="truncate">{f.name}</span>
                        <button
                          type="button"
                          aria-label={`Retirer ${f.name}`}
                          data-testid={`form-file-remove-${i}`}
                          onClick={() => setFiles(files.filter((_, j) => j !== i))}
                          className="ml-3 text-anthracite/50 hover:text-nuit"
                        >
                          <X size={14} strokeWidth={1.5} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="md:col-span-2 flex flex-col gap-5 pt-2">
                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={sending}
                  className="inline-flex items-center justify-center gap-3 bg-nuit px-8 py-4 font-accent text-sm font-medium tracking-[0.12em] uppercase text-casse hover:bg-anthracite disabled:opacity-60 transition-colors duration-300"
                >
                  {sending && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
                  {sending ? "Envoi en cours…" : "Envoyer ma demande"}
                </button>
                <p className="text-xs leading-relaxed text-anthracite/55" data-testid="contact-privacy-note">
                  Vos informations restent confidentielles et sont utilisées uniquement pour traiter votre demande.
                </p>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
